import { useState } from 'react'
import { Building2, MapPin, Users, Plus } from 'lucide-react'
import adminApi from '../../../services/adminApi'
import { SPACE_TYPES, INITIAL_SPACE_FORM } from '../constants'
import CreateSpaceModal from '../modals/CreateSpaceModal'

const SpacesTab = ({ spaces, facilitators, onRefresh, onEnsureFacilitators }) => {
  const [showCreateSpaceModal, setShowCreateSpaceModal] = useState(false)
  const [spaceForm, setSpaceForm] = useState(INITIAL_SPACE_FORM)
  const [creatingSpace, setCreatingSpace] = useState(false)
  const [spaceFormError, setSpaceFormError] = useState('')

  const formatSpaceType = (type) => {
    return SPACE_TYPES.find(t => t.value === type)?.label || type
  }

  const handleOpenCreateSpaceModal = async () => {
    setSpaceForm(INITIAL_SPACE_FORM)
    setSpaceFormError('')
    if (onEnsureFacilitators) await onEnsureFacilitators()
    setShowCreateSpaceModal(true)
  }

  const handleCloseCreateSpaceModal = () => {
    setShowCreateSpaceModal(false)
    setSpaceForm(INITIAL_SPACE_FORM)
    setSpaceFormError('')
  }

  const handleSpaceFormChange = (e) => {
    const { name, value } = e.target
    setSpaceForm(prev => ({ ...prev, [name]: value }))
    setSpaceFormError('')
  }

  const handleCreateSpace = async (e) => {
    e.preventDefault()
    setSpaceFormError('')

    if (!spaceForm.facilitatorProfileId) {
      setSpaceFormError('Please select a facilitator')
      return
    }
    if (!spaceForm.name.trim()) {
      setSpaceFormError('Please enter a space name')
      return
    }
    if (!spaceForm.spaceType) {
      setSpaceFormError('Please select a space type')
      return
    }

    setCreatingSpace(true)

    try {
      const payload = {
        ...spaceForm,
        facilitatorProfileId: parseInt(spaceForm.facilitatorProfileId, 10),
        capacity: spaceForm.capacity ? parseInt(spaceForm.capacity, 10) : null,
      }

      await adminApi.post('/api/admin/spaces', payload)
      await onRefresh(true)
      handleCloseCreateSpaceModal()
    } catch (err) {
      setSpaceFormError(err.response?.data?.message || 'Failed to create space')
    } finally {
      setCreatingSpace(false)
    }
  }

  if (!spaces) return null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Building2 className="w-6 h-6" />
          Spaces ({spaces.length})
        </h3>
        <button
          onClick={handleOpenCreateSpaceModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Space
        </button>
      </div>

      {spaces.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No spaces created yet</p>
          <button
            onClick={handleOpenCreateSpaceModal}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create your first space
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/10">
            <thead>
              <tr>
                {['Name', 'Type', 'Location', 'Facilitator', 'Capacity', 'Status', 'Created'].map(header => (
                  <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {spaces.map((space) => (
                <tr key={space.id} className="hover:bg-white/5">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">{space.name}</div>
                    <div className="text-xs text-gray-400">{space.slug}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300">
                      {formatSpaceType(space.spaceType)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {space.city || space.addressLine1 ? (
                      <div className="flex items-start gap-1 text-sm text-gray-300">
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500" />
                        <div>
                          {space.addressLine1 && <div>{space.addressLine1}</div>}
                          <div>
                            {[space.city, space.state].filter(Boolean).join(', ')}
                            {space.pincode && ` - ${space.pincode}`}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">Not specified</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {space.facilitator ? (
                      <div>
                        <div className="text-sm font-medium text-white">{space.facilitator.name}</div>
                        <div className="text-xs text-gray-400">{space.facilitator.email}</div>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    {space.capacity ? (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {space.capacity}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      space.active 
                        ? 'bg-green-500/20 text-green-300' 
                        : 'bg-red-500/20 text-red-300'
                    }`}>
                      {space.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    {new Date(space.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateSpaceModal
        show={showCreateSpaceModal}
        onClose={handleCloseCreateSpaceModal}
        spaceForm={spaceForm}
        onFormChange={handleSpaceFormChange}
        onSubmit={handleCreateSpace}
        creatingSpace={creatingSpace}
        spaceFormError={spaceFormError}
        facilitators={facilitators}
      />
    </div>
  )
}

export default SpacesTab
